import { defineStore, storeToRefs } from "pinia";
import { ref, computed } from "vue";
import { createCondition } from "@stores/survey/cardPayloadHandler";
import { useSurveyCardsStore } from "@stores/survey/surveyCards";
import { useCurrentCardStore } from "@stores/survey/currentCard/currentCard";
import { computedCardOptions, optionToCardToken } from "@utils/computeCardOptions";
import { computedValuesOptions } from "@utils/computeConditionValuesOptions";
import { conditionCardToken } from "@utils/conditionData";
import {
  allowsTypedValues,
  conditionTypeOptions,
  conditionValueInput,
  isComparisonType,
  resetConditionValues,
} from "@utils/conditionOperators";

export const useConditionsStore = defineStore("conditions", () => {
  const surveyCardsStore = useSurveyCardsStore();
  const currentCardStore = useCurrentCardStore();
  const { preceedingCards, currentCard } = storeToRefs(currentCardStore);
  const { findCardByToken } = surveyCardsStore;
  // conditions
  const elementToUpdate = ref({
    Answer: "answers",
    Image: "answers",
    Item: "items",
  });
  const handleAddCondition = (conditions, elementName, elementIndex) => {
    if (conditions.length === 0 && elementName != null && elementIndex !== -1) {
      const element = elementToUpdate.value?.[elementName];
      currentCard.value.specs[element][elementIndex].conditions = conditions;
    }

    addCondition(conditions);
  };
  const nearestCardToken = () => {
    const nearest = cardTokenOptions.value.at(-1);
    return nearest ? optionToCardToken(nearest.value) : null;
  };
  const addCondition = (conditions) => conditions.push({ ...createCondition(), card_token: nearestCardToken() });
  const removeCondition = (conditions, index) => conditions.splice(index, 1);

  // card_token
  const allowedTypes = ref([
    "q_single",
    "q_multiple",
    "q_ranking",
    "q_rating",
    "q_slider",
    "q_numeric",
    "q_city",
    "q_image_choice",
    "q_image_choice_multiple",
    "o_ab",
    "o_cells",
    "o_custom",
    "o_user_attributes",
    "q_open",
    "q_open_multiple",
    "q_matrix",
    "q_email",
    "q_image",
    "q_video",
    "q_audio",
    "q_zipcode",
    "q_nps",
    "q_language",
    "o_quota",
  ]);

  const additionalAllowedTypes = ref(["q_hidden", "o_loop"]);

  const cardTokenOptions = computed(() => {
    const isCardAllowed = (card) => {
      const isTypeAllowed = allowedTypes.value.includes(card.type) || additionalAllowedTypes.value.includes(card.type);
      const isNotInLoop = !currentCard.value.specs?.loop_token ? !card.specs?.loop_token : true;
      const areInSameLoop =
        currentCard.value.specs?.loop_token && card.specs?.loop_token
          ? card.specs.loop_token === currentCard.value.specs.loop_token
          : true;
      const isNotEndLoop = !(card.specs?.position === "end");
      return isTypeAllowed && isNotInLoop && areInSameLoop && isNotEndLoop;
    };
    const filteredCards = preceedingCards.value.filter(
      //If currentCard is not part of a loop, it cannot have a condition on the cards that are part of a loop.
      (card) => isCardAllowed(card),
    );
    const filteredCardOptions = computedCardOptions({
      cards: filteredCards,
      handleCustom: true,
      handleOUserAttributes: true,
      handleLoop: true,
    });
    return filteredCardOptions;
  });
  const updateCardToken = (condition, cardToken) => (condition.card_token = cardToken);
  const handleCardTokenUpdate = (condition, cardTokenOption) => {
    const cardToken = optionToCardToken(cardTokenOption);
    updateCardToken(condition, cardToken);
    resetConditionValues(condition);
    if (!typeOptionsFor(condition).some((o) => o.value === condition.type)) condition.type = "is";
  };

  const conditionSourceCard = (condition) => findCardByToken({ token: conditionCardToken(condition?.card_token) });
  const typeOptionsFor = (condition) => conditionTypeOptions(conditionSourceCard(condition), condition?.type);
  const valueInputFor = (condition) => conditionValueInput(conditionSourceCard(condition));
  const updateType = (condition, value) => {
    if (isComparisonType(value) !== isComparisonType(condition.type)) resetConditionValues(condition);
    condition.type = value;
  };
  const updateValue = (condition, value) => (condition.value = value);
  const updateBound = (condition, bound, value) => (condition[bound] = value);

  // values
  const valuesOptions = ({ card_token } = {}) => {
    const card = findCardByToken({ token: conditionCardToken(card_token) });
    if (!card) return;
    let sourceCard;
    if (card.type === "o_custom") {
      sourceCard = findCardByToken({ token: card.specs?.question_reference });
    } else if (card.type === "o_loop") {
      sourceCard = findCardByToken({ token: card.specs?.source_question_token });
    } else {
      sourceCard = undefined;
    }
    return computedValuesOptions(card, sourceCard);
  };
  const allowCreateValuesOptions = ({ card_token }) =>
    allowsTypedValues(findCardByToken({ token: conditionCardToken(card_token) }));
  const updateValues = (condition, values) => (condition.values = values);

  // operator
  const operatorOptions = ref([
    { label: "Or", value: "or" },
    { label: "And", value: "and" },
  ]);
  const updateOperator = (condition, value) => (condition.operator = value);

  return {
    // conditions
    handleAddCondition,
    removeCondition,
    nearestCardToken,

    // card_token
    cardTokenOptions,
    allowedTypes,
    handleCardTokenUpdate,

    // type
    typeOptionsFor,
    valueInputFor,
    updateType,
    updateValue,
    updateBound,

    // values
    valuesOptions,
    allowCreateValuesOptions,
    updateValues,

    // operator
    operatorOptions,
    updateOperator,
  };
});
